import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { getDotdKit } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Check, Download, Sparkles, ArrowLeft, Calendar } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_META = {
  x: { label: "X (Twitter)", color: "#000" },
  linkedin: { label: "LinkedIn", color: "#0a66c2" },
  instagram: { label: "Instagram", color: "#c13584" },
  generic: { label: "Anywhere else", color: "#5c5c5c" },
};

function formatDate(iso) {
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

export default function DotdKit() {
  const { session_id } = useParams();
  const [searchParams] = useSearchParams();
  const isWelcome = searchParams.get("welcome") === "1";
  const [kit, setKit] = useState(null);
  const [state, setState] = useState({ loading: true, err: null });
  const [copiedKey, setCopiedKey] = useState(null);
  const [retries, setRetries] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, err: null });
    let attempts = 0;
    const tryFetch = async () => {
      attempts += 1;
      try {
        const k = await getDotdKit(session_id);
        if (cancelled) return;
        setKit(k);
        setState({ loading: false, err: null });
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        // Payment not confirmed yet — Stripe sometimes takes a few seconds.
        // Retry up to 8x every 1.5s before giving up.
        if (status === 402 && attempts < 8) {
          setRetries(attempts);
          setTimeout(tryFetch, 1500);
        } else {
          setState({
            loading: false,
            err: e?.response?.data?.detail || "Couldn't load your kit. Refresh in a moment.",
          });
        }
      }
    };
    tryFetch();
    return () => { cancelled = true; };
  }, [session_id]);

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedKey(null), 1600);
    } catch {
      toast.error("Copy blocked by browser");
    }
  };

  if (state.loading) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-20 text-center" data-testid="kit-loading">
        <div className="mono text-xs tracking-widest text-[#5c5c5c] mb-3">
          {retries > 0 ? `WAITING FOR STRIPE (${retries}/8)…` : "LOADING YOUR KIT…"}
        </div>
        <div className="w-16 h-16 mx-auto rounded-full border-4 border-[#2d2a32] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (state.err || !kit) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-20 text-center" data-testid="kit-error">
        <p className="serif text-4xl mb-3">Kit not ready yet.</p>
        <p className="text-sm text-[#5c5c5c] mb-6">{state.err}</p>
        <Link
          to="/sponsor"
          className="inline-flex items-center gap-2 h-11 px-5 border-2 border-[#2d2a32] rounded-full text-[11px] tracking-[0.22em] font-bold bg-white"
        >
          <ArrowLeft className="w-4 h-4" /> BACK TO SPONSOR
        </Link>
      </div>
    );
  }

  const cardOg = `${API}${kit.share_card_url}`;
  const cardSquare = `${API}${kit.share_card_square_url}`;

  return (
    <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-10" data-testid="dotd-kit-page">
      {isWelcome && (
        <div
          className="mb-8 border-2 border-[#2d2a32] rounded-2xl p-5 bg-[#7dbb6e]/15 flex items-start gap-3"
          data-testid="kit-welcome-banner"
        >
          <div className="w-10 h-10 rounded-full border-2 border-[#2d2a32] bg-[#7dbb6e] text-white flex items-center justify-center shrink-0">
            <Check className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="serif text-2xl leading-tight">Payment confirmed — your slot is locked in.</div>
            <p className="text-sm text-[#3a3a38] mt-1 leading-snug">
              A confirmation email is on the way to your inbox. Below is everything you need to shout about it.
            </p>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="mb-10">
        <div className="text-[10px] tracking-[0.35em] text-[#5c5c5c] mb-2 inline-flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> DOTD MARKETING KIT
        </div>
        <h1 className="serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
          You&#39;re at the <span className="italic text-[#ff6b6b]">top.</span>
        </h1>
        <p className="mt-4 text-lg text-[#3a3a38] max-w-2xl leading-relaxed">
          <strong>{kit.store}</strong> is the Deal of the Day. Post the card, paste the caption, and let your audience know.
        </p>
      </section>

      {/* Booked dates */}
      <section className="mb-10 border-2 border-[#2d2a32] rounded-2xl p-5 bg-white hard-shadow-sm" data-testid="kit-dates">
        <div className="text-[10px] tracking-[0.3em] text-[#5c5c5c] flex items-center gap-2 mb-3">
          <Calendar className="w-3 h-3" /> YOUR SCHEDULED DATES
        </div>
        <div className="flex flex-wrap gap-2">
          {kit.dates.map((d) => (
            <span
              key={d}
              data-testid={`kit-date-${d}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#fde68a] border-2 border-[#2d2a32] rounded-full font-bold text-sm"
            >
              {formatDate(d)}
            </span>
          ))}
        </div>
      </section>

      {/* Share cards */}
      <section className="mb-10">
        <h2 className="serif text-3xl mb-4">1 · Download your graphics</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ShareCardBlock
            label="Landscape · 1200 × 630 (X, LinkedIn, Facebook, blog)"
            url={cardOg}
            filename={`dotd-${kit.store}-landscape.png`}
            testid="share-card-og"
          />
          <ShareCardBlock
            label="Square · 1080 × 1080 (Instagram, Facebook, LinkedIn)"
            url={cardSquare}
            filename={`dotd-${kit.store}-square.png`}
            testid="share-card-square"
          />
        </div>
      </section>

      {/* Captions */}
      <section className="mb-14">
        <h2 className="serif text-3xl mb-4">2 · Copy-paste captions</h2>
        <div className="space-y-3">
          {Object.entries(kit.captions).map(([key, text]) => {
            const meta = PLATFORM_META[key] || { label: key.toUpperCase(), color: "#5c5c5c" };
            const isCopied = copiedKey === key;
            return (
              <div
                key={key}
                data-testid={`kit-caption-${key}`}
                className="border-2 border-[#2d2a32] rounded-xl p-4 bg-white flex flex-col md:flex-row gap-4 items-start"
              >
                <div className="shrink-0">
                  <span
                    className="mono text-[10px] tracking-[0.25em] font-black px-3 py-1 border-2 border-[#2d2a32] rounded-md text-white"
                    style={{ background: meta.color }}
                  >
                    {meta.label}
                  </span>
                </div>
                <div className="flex-1 min-w-0 whitespace-pre-line text-sm md:text-[15px] text-[#2d2a32] leading-relaxed">
                  {text}
                </div>
                <button
                  onClick={() => copy(key, text)}
                  data-testid={`kit-copy-${key}`}
                  className={`h-10 px-4 border-2 border-[#2d2a32] rounded-full text-[11px] tracking-[0.22em] font-bold inline-flex items-center gap-2 shrink-0 ${
                    isCopied ? "bg-[#7dbb6e] text-white" : "bg-white hover:bg-[#f7c873]"
                  }`}
                >
                  {isCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {isCopied ? "COPIED" : "COPY"}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Direct link */}
      <section className="mb-10 border-2 border-dashed border-[#2d2a32] rounded-2xl p-5 bg-[#fff2d6]">
        <div className="text-[10px] tracking-[0.3em] text-[#5c5c5c] mb-2">DIRECT LINK</div>
        <div className="flex items-center gap-3 flex-wrap">
          <a
            href={kit.site_url}
            target="_blank"
            rel="noopener"
            data-testid="kit-site-url"
            className="mono text-sm md:text-base font-bold underline decoration-2 text-[#2d2a32] break-all"
          >
            {kit.site_url}
          </a>
          <button
            onClick={() => copy("site", kit.site_url)}
            data-testid="kit-copy-site"
            className="h-9 px-3 border-2 border-[#2d2a32] rounded-full text-[11px] tracking-[0.22em] font-bold inline-flex items-center gap-1 bg-white hover:bg-[#f7c873]"
          >
            {copiedKey === "site" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            COPY URL
          </button>
        </div>
      </section>

      <Link
        to="/sponsor"
        className="inline-flex items-center gap-2 h-11 px-5 border-2 border-[#2d2a32] rounded-full text-[11px] tracking-[0.22em] font-bold bg-white hover:bg-[#f7c873]"
      >
        <ArrowLeft className="w-4 h-4" /> BACK TO SPONSOR
      </Link>
    </div>
  );
}

function ShareCardBlock({ label, url, filename, testid }) {
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement("a");
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
      toast.success("Downloaded — post away 🚀");
    } catch {
      toast.error("Couldn't download — right-click the image and save it instead.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="border-2 border-[#2d2a32] rounded-2xl p-4 bg-white hard-shadow-sm"
      data-testid={testid}
    >
      <div className="text-[10px] tracking-[0.25em] text-[#5c5c5c] mb-3">{label}</div>
      <div className="border-2 border-dashed border-[#2d2a32]/40 rounded-lg overflow-hidden mb-3">
        <img
          src={url}
          alt="Share card preview"
          data-testid={`${testid}-img`}
          loading="lazy"
          className="block w-full h-auto"
        />
      </div>
      <button
        onClick={download}
        disabled={downloading}
        data-testid={`${testid}-download`}
        className="w-full h-11 border-2 border-[#2d2a32] rounded-full bg-[#2d2a32] hover:bg-[#ff6b6b] text-white text-[11px] tracking-[0.22em] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
      >
        <Download className="w-4 h-4" />
        {downloading ? "DOWNLOADING…" : "DOWNLOAD PNG"}
      </button>
    </div>
  );
}
